import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Eye, Edit, Trash2, UserPlus, Calendar, MessageSquare, X, DollarSign, PhoneCall, AlertTriangle } from 'lucide-react';
import { Lead, Make, Model, ServiceType, LeadFollowUp, LeadSummary, LeadCallLog } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { LEAD_STATUSES } from '../constants/leadStatuses';

const CallRecordingPlayer: React.FC<{ log: LeadCallLog }> = ({ log }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const loadAudio = async () => {
      if (!log.recordingUrl) return;

      try {
        setLoadingAudio(true);
        setAudioError(null);
        const blob = await apiService.getLeadCallRecordingBlob(log.leadId, log.id);
        const playableBlob = blob.type ? blob : new Blob([blob], { type: 'audio/mp4' });
        objectUrl = URL.createObjectURL(playableBlob);

        if (!cancelled) {
          setAudioUrl(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        console.error('Error loading call recording:', error);
        if (!cancelled) {
          setAudioError('Recording could not be loaded in the browser.');
        }
      } finally {
        if (!cancelled) {
          setLoadingAudio(false);
        }
      }
    };

    loadAudio();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [log.id, log.leadId, log.recordingUrl]);

  if (!log.recordingUrl) {
    return (
      <div className="text-xs text-gray-500 italic">
        No recording uploaded for this call.
      </div>
    );
  }

  if (loadingAudio) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"></div>
        Loading recording...
      </div>
    );
  }

  if (audioError || !audioUrl) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
        <p>{audioError || 'Recording is not ready for playback.'}</p>
        <a
          href={log.recordingUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-flex font-medium text-amber-800 underline"
        >
          Open uploaded file
        </a>
      </div>
    );
  }

  return (
    <audio controls preload="metadata" src={audioUrl} className="w-full">
      Your browser does not support audio playback.
    </audio>
  );
};

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [followUpDateFilter, setFollowUpDateFilter] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Edit Lead Form State
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [priority, setPriority] = useState('');
  const [notes, setNotes] = useState('');

  // Cascading dropdowns for edit modal
  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedMake, setSelectedMake] = useState<number | null>(null);
  const [selectedModel, setSelectedModel] = useState<number | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<number | null>(null);

  // Follow-up Modal State
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedLeadForFollowUp, setSelectedLeadForFollowUp] = useState<Lead | null>(null);
  const [followUps, setFollowUps] = useState<LeadFollowUp[]>([]);
  const [loadingFollowUps, setLoadingFollowUps] = useState(false);
  const [isEditingFollowUp, setIsEditingFollowUp] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<LeadFollowUp | null>(null);
  
  // Follow-up Form State
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpType, setFollowUpType] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [followUpOutcome, setFollowUpOutcome] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');

  // Summary state
  const [leadSummary, setLeadSummary] = useState<LeadSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Call history modal
  const [showCallHistoryModal, setShowCallHistoryModal] = useState(false);
  const [selectedLeadForCalls, setSelectedLeadForCalls] = useState<Lead | null>(null);
  const [callLogs, setCallLogs] = useState<LeadCallLog[]>([]);
  const [loadingCallLogs, setLoadingCallLogs] = useState(false);

  useEffect(() => {
    loadLeads();
    loadMakes();
    loadLeadSummary();
  }, [currentPage, pageSize, searchTerm, statusFilter, sourceFilter, priorityFilter, followUpDateFilter]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      // Convert follow-up date filter to Date if provided
      const followUpDate = followUpDateFilter ? new Date(followUpDateFilter) : undefined;
      const response = await apiService.searchLeads(
        currentPage,
        pageSize,
        searchTerm,
        followUpDate, // startDate - using follow-up date as start date
        followUpDate, // endDate - using same date as end date for exact match
        statusFilter || undefined,
        sourceFilter || undefined,
        priorityFilter || undefined,
        followUpDateFilter || undefined // followUpDate parameter
      );

      if (response.isSuccess && response.entity) {
        setLeads(response.entity.items);
        setTotalItems(response.entity.totalCount);
        setTotalPages(response.entity.totalPages);
      } else {
        console.error('Failed to load leads:', response.errors);
        toast.error('Failed to load leads');
        setLeads([]);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Error loading leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMakes = async () => {
    try {
      const response = await apiService.getMakes();
      if (response.isSuccess && response.entity) {
        setMakes(response.entity);
      }
    } catch (error) {
      console.error('Error loading makes:', error);
    }
  };

  const loadLeadSummary = async () => {
    try {
      setLoadingSummary(true);
      const today = new Date();
      // Get local date string in YYYY-MM-DD format to avoid timezone issues
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      
      // Create date objects for today (local timezone)
      const startDate = new Date(todayString + 'T00:00:00');
      const endDate = new Date(todayString + 'T23:59:59');
      
      const response = await apiService.getLeadSummary(startDate, endDate);
      console.log('Lead Summary API Response:', response);
      
      // Handle both ValidationResult format and direct data format
      if ('isSuccess' in response && response.isSuccess && response.entity) {
        // Standard ValidationResult format
        console.log('Setting lead summary data:', response.entity);
        setLeadSummary(response.entity);
      } else if ('totalLeadsCount' in response) {
        // Direct data format (API returns data directly)
        console.log('Setting lead summary data (direct format):', response);
        setLeadSummary(response as LeadSummary);
      } else {
        console.error('Failed to load lead summary:', ('errors' in response ? response.errors : 'Unknown error'));
      }
    } catch (error) {
      console.error('Error loading lead summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadModels = async (makeId: number) => {
    try {
      const response = await apiService.getModels(makeId);
      if (response.isSuccess && response.entity) {
        setModels(response.entity);
      }
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const loadServiceTypes = async (modelId: number) => {
    try {
      const response = await apiService.getServiceTypes(modelId);
              if (response.isSuccess && response.entity) {
          setServiceTypes(response.entity);
        }
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  };

  const handleMakeChange = (makeId: number) => {
    setSelectedMake(makeId);
    setSelectedModel(null);
    setSelectedServiceType(null);
    setModels([]);
    setServiceTypes([]);
    if (makeId) {
      loadModels(makeId);
    }
  };

  const handleModelChange = (modelId: number) => {
    setSelectedModel(modelId);
    setSelectedServiceType(null);
    setServiceTypes([]);
    if (modelId) {
      loadServiceTypes(modelId);
    }
  };



  const handleDeleteLead = async (leadId: number) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        const response = await apiService.deleteLead(leadId);
        if (response.isSuccess) {
          toast.success('Lead deleted successfully');
          loadLeads(); // Reload the leads list
        } else {
          toast.error('Failed to delete lead');
        }
      } catch (error) {
        console.error('Error deleting lead:', error);
        toast.error('Error deleting lead');
      }
    }
  };

  const handleEditLead = (lead: Lead) => {
    navigate(`/leads/edit/${lead.id}`);
  };

  const handleUpdateLead = async () => {
    if (!editingLead) return;
    
    try {
      const leadData = {
        ...editingLead,
        name: customerName,
        mobile: customerMobile,
        email: customerEmail || '',
        address: customerAddress || '',
        source: leadSource,
        priority,
        notes,
        updatedAt: new Date().toISOString(),
        deviceType: models.find(m => m.id === selectedModel)?.name || editingLead.deviceType,
        deviceModel: models.find(m => m.id === selectedModel)?.name || editingLead.deviceModel,
        serviceRequired: serviceTypes.find(s => s.id === selectedServiceType)?.serviceName || editingLead.serviceRequired,
      };

      const response = await apiService.updateLead(editingLead.id, leadData);
      
      if (response.isSuccess) {
        toast.success('Lead updated successfully');
        setShowEditModal(false);
        setEditingLead(null);
        resetForm();
        loadLeads(); // Reload the leads list
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to update lead');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Error updating lead');
    }
  };

  const resetForm = () => {
    setCustomerName('');
    setCustomerMobile('');
    setCustomerEmail('');
    setCustomerAddress('');
    setLeadSource('');
    setPriority('');
    setNotes('');
    setSelectedMake(null);
    setSelectedModel(null);
    setSelectedServiceType(null);
    setModels([]);
    setServiceTypes([]);
    setEditingLead(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadLeads();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Follow-up Functions
  const handleOpenFollowUpModal = async (lead: Lead) => {
    setSelectedLeadForFollowUp(lead);
    setShowFollowUpModal(true);
    await loadFollowUps(lead.id);
  };

  const loadFollowUps = async (leadId: number) => {
    try {
      setLoadingFollowUps(true);
      const response = await apiService.getLeadFollowUps(leadId);
      if (response.isSuccess && response.entity) {
        setFollowUps(response.entity);
      } else {
        setFollowUps([]);
      }
    } catch (error) {
      console.error('Error loading follow-ups:', error);
      toast.error('Error loading follow-ups');
      setFollowUps([]);
    } finally {
      setLoadingFollowUps(false);
    }
  };

  const handleCreateFollowUp = () => {
    setIsEditingFollowUp(false);
    setEditingFollowUp(null);
    resetFollowUpForm();
  };

  const handleEditFollowUp = (followUp: LeadFollowUp) => {
    setIsEditingFollowUp(true);
    setEditingFollowUp(followUp);
    // Handle date conversion - API may return string or Date
    const followUpDateValue = followUp.followUpDate instanceof Date 
      ? followUp.followUpDate 
      : new Date(followUp.followUpDate);
    setFollowUpDate(followUpDateValue.toISOString().split('T')[0]);
    setFollowUpType(followUp.followUpType);
    setFollowUpNotes(followUp.notes);
    setFollowUpOutcome(followUp.outcome);
    if (followUp.nextFollowUpDate) {
      const nextDateValue = followUp.nextFollowUpDate instanceof Date 
        ? followUp.nextFollowUpDate 
        : new Date(followUp.nextFollowUpDate);
      setNextFollowUpDate(nextDateValue.toISOString().split('T')[0]);
    } else {
      setNextFollowUpDate('');
    }
  };

  const resetFollowUpForm = () => {
    setFollowUpDate('');
    setFollowUpType('');
    setFollowUpNotes('');
    setFollowUpOutcome('');
    setNextFollowUpDate('');
  };

  const handleSaveFollowUp = async () => {
    if (!selectedLeadForFollowUp) return;

    if (!followUpDate || !followUpType || !followUpNotes) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const followUpData: any = {
        leadId: selectedLeadForFollowUp.id,
        followUpDate: new Date(followUpDate).toISOString(),
        followUpType,
        notes: followUpNotes,
        outcome: followUpOutcome || '',
      };
      
      if (nextFollowUpDate) {
        followUpData.nextFollowUpDate = new Date(nextFollowUpDate).toISOString();
      }

      let response;
      if (isEditingFollowUp && editingFollowUp) {
        // Include the id in the request body for update - API requires id to match URL parameter
        followUpData.id = editingFollowUp.id;
        response = await apiService.updateLeadFollowUp(
          selectedLeadForFollowUp.id,
          editingFollowUp.id,
          followUpData as LeadFollowUp
        );
      } else {
        response = await apiService.createLeadFollowUp(
          selectedLeadForFollowUp.id,
          followUpData as LeadFollowUp
        );
      }

      if (response.isSuccess) {
        toast.success(`Follow-up ${isEditingFollowUp ? 'updated' : 'created'} successfully`);
        resetFollowUpForm();
        setIsEditingFollowUp(false);
        setEditingFollowUp(null);
        await loadFollowUps(selectedLeadForFollowUp.id);
        loadLeads(); // Refresh leads to update nextFollowUpDate
      } else {
        toast.error(response.errors?.join(', ') || `Failed to ${isEditingFollowUp ? 'update' : 'create'} follow-up`);
      }
    } catch (error) {
      console.error('Error saving follow-up:', error);
      toast.error('Error saving follow-up');
    }
  };

  const handleCloseFollowUpModal = () => {
    setShowFollowUpModal(false);
    setSelectedLeadForFollowUp(null);
    setIsEditingFollowUp(false);
    setEditingFollowUp(null);
    resetFollowUpForm();
    setFollowUps([]);
  };

  const handleOpenCallHistoryModal = async (lead: Lead) => {
    setSelectedLeadForCalls(lead);
    setShowCallHistoryModal(true);
    setLoadingCallLogs(true);
    try {
      const response = await apiService.getLeadCallLogsByPhone(lead.mobile);
      if (response.isSuccess && response.entity) {
        setCallLogs(response.entity);
      } else {
        setCallLogs([]);
        toast.error(response.errors?.join(', ') || 'Failed to load call history');
      }
    } catch (error) {
      console.error('Error loading call history:', error);
      toast.error('Error loading call history');
      setCallLogs([]);
    } finally {
      setLoadingCallLogs(false);
    }
  };

  const handleCloseCallHistoryModal = () => {
    setShowCallHistoryModal(false);
    setSelectedLeadForCalls(null);
    setCallLogs([]);
  };

  const formatDuration = (seconds?: number | null) => {
    if (!seconds && seconds !== 0) return '-';
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

   const getStatusBadge = (leadStatus: string) => {
     const statusColors: { [key: string]: string } = {
       'New': 'bg-blue-100 text-blue-800',
       'Connected': 'bg-green-100 text-green-800',
       'Follow Up': 'bg-sky-100 text-sky-800',
       'Not Connected': 'bg-slate-100 text-slate-700',
       'Contacted': 'bg-yellow-100 text-yellow-800',
       'Quotation Send': 'bg-violet-100 text-violet-800',
       'Interested': 'bg-emerald-100 text-emerald-800',
       'Price High': 'bg-amber-100 text-amber-800',
       'Stock Not available': 'bg-orange-100 text-orange-800',
       'In Progress': 'bg-indigo-100 text-indigo-800',
       'Negotiation': 'bg-teal-100 text-teal-800',
       'On Hold': 'bg-gray-100 text-gray-700',
       'Call Back Requested': 'bg-cyan-100 text-cyan-800',
       'No Response': 'bg-red-50 text-red-700',
       'Wrong Number': 'bg-rose-100 text-rose-800',
       'Qualified': 'bg-green-100 text-green-800',
       'Closed - Won': 'bg-emerald-100 text-emerald-800',
       'Closed - Lost': 'bg-red-100 text-red-800',
       'Lost': 'bg-red-100 text-red-800',
       'Converted': 'bg-emerald-100 text-emerald-800',
     };
     return statusColors[leadStatus] || 'bg-gray-100 text-gray-800';
   };

  const getPriorityBadge = (priority: string) => {
    const priorityColors: { [key: string]: string } = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return priorityColors[priority] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
            {/* Users icon removed as per new_code */}
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
            <p className="text-sm text-gray-500">Manage customer inquiries and potential sales</p>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/leads/create')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Lead
        </button>
      </div>

      {/* Lead Summary */}
      {leadSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{leadSummary.totalLeadsCount}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Final Amount</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">₹{leadSummary.totalFinalAmount.toLocaleString('en-IN')}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Follow-ups Today</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{leadSummary.leadsWithFollowUpToday}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Follow-ups Tomorrow</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{leadSummary.leadsWithFollowUpTomorrow}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lead Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Statuses</option>
                {LEAD_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Sources</option>
                <option value="Website">Website</option>
                <option value="Phone">Phone</option>
                <option value="Email">Email</option>
                <option value="Social Media">Social Media</option>
                <option value="Referral">Referral</option>
                <option value="Walk-in">Walk-in</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Priorities</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Follow-up Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={followUpDateFilter}
                  onChange={(e) => setFollowUpDateFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Filter by follow-up date"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </button>
              {(followUpDateFilter || searchTerm || statusFilter || sourceFilter || priorityFilter) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                    setSourceFilter('');
                    setPriorityFilter('');
                    setFollowUpDateFilter('');
                    setCurrentPage(1);
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Clear Filters
                </button>
              )}
            </div>
            
            <div className="text-sm text-gray-500">
              {totalItems} total leads
            </div>
          </div>
        </form>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            {/* Users icon removed as per new_code */}
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
            <p className="text-gray-500">Create your first lead to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Mobile</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Follow Up</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="text-xs font-semibold text-gray-800">{lead.name}</div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="text-xs text-gray-600">{lead.mobile}</div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {lead.nextFollowUpDate ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            Next: {new Date(lead.nextFollowUpDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        ) : lead.lastContactDate ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            Last: {new Date(lead.lastContactDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            None
                          </span>
                        )}
                        <button
                          onClick={() => handleOpenFollowUpModal(lead)}
                          className="text-indigo-400 hover:text-indigo-700 transition-colors"
                          title="Manage Follow-ups"
                        >
                          <Calendar className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(lead.status)}`}>
                        {lead.status || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="text-xs text-gray-500">{lead.source || '-'}</div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        {lead.createdAt ? new Date(lead.createdAt).toLocaleString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Asia/Kolkata'
                        }) : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button className="text-indigo-400 hover:text-indigo-700 transition-colors">
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleOpenCallHistoryModal(lead)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          title="Call History"
                        >
                          <PhoneCall className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleEditLead(lead)}
                          className="text-green-500 hover:text-green-700 transition-colors"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && leads.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, currentPage - 2) + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      page === currentPage
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Edit Lead Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Lead</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {/* X icon removed as per new_code */}
                </button>
              </div>

              <div className="space-y-4">
                {/* Customer Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name *</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter customer name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                    <input
                      type="tel"
                      value={customerMobile}
                      onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="10-digit mobile number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                    <select
                      value={leadSource}
                      onChange={(e) => setLeadSource(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Source</option>
                      <option value="Website">Website</option>
                      <option value="Phone">Phone</option>
                      <option value="Email">Email</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Referral">Referral</option>
                      <option value="Walk-in">Walk-in</option>
                    </select>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Priority</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter address"
                    />
                  </div>
                </div>

                {/* Device Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                    <select
                      value={selectedMake || ''}
                      onChange={(e) => handleMakeChange(e.target.value ? parseInt(e.target.value) : 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Make</option>
                      {makes.map((make) => (
                        <option key={make.id} value={make.id}>
                          {make.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                    <select
                      value={selectedModel || ''}
                      onChange={(e) => handleModelChange(e.target.value ? parseInt(e.target.value) : 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={!selectedMake}
                    >
                      <option value="">Select Model</option>
                      {models.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
                    <select
                      value={selectedServiceType || ''}
                      onChange={(e) => setSelectedServiceType(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={!selectedModel}
                    >
                      <option value="">Select Service</option>
                      {serviceTypes.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.serviceName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Add any additional notes..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLead}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {/* Save icon removed as per new_code */}
                  Update Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call History Modal */}
      {showCallHistoryModal && selectedLeadForCalls && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseCallHistoryModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Customer Call History</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedLeadForCalls.name} ({selectedLeadForCalls.mobile})
                  </p>
                </div>
                <button
                  onClick={handleCloseCallHistoryModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {loadingCallLogs ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : callLogs.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <PhoneCall className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No call history found for this phone number.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {callLogs.map((log) => (
                    <div key={log.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(log.leadStatusAfterCall || log.callStatus)}`}>
                              {log.disposition || log.callStatus}
                            </span>
                            {log.isSuspicious && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                <AlertTriangle className="h-3 w-3" />
                                Review
                              </span>
                            )}
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            {log.callStartedAt ? new Date(log.callStartedAt).toLocaleString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'Asia/Kolkata'
                            }) : '-'}
                            {' '}• Duration {formatDuration(log.durationSeconds)}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            Telecaller: {log.telecallerName || '-'}
                          </div>
                        </div>
                      </div>

                      {log.notes && (
                        <p className="mt-3 text-xs text-gray-700 bg-white rounded-md p-2 border border-gray-100">
                          {log.notes}
                        </p>
                      )}

                      {log.isSuspicious && log.suspiciousReason && (
                        <p className="mt-2 text-xs font-medium text-amber-700">
                          {log.suspiciousReason}
                        </p>
                      )}

                      <div className="mt-3">
                        <CallRecordingPlayer log={log} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && selectedLeadForFollowUp && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseFollowUpModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Follow-up Management</h3>
                  <p className="text-sm text-gray-500 mt-1">Lead: {selectedLeadForFollowUp.name} ({selectedLeadForFollowUp.mobile})</p>
                </div>
                <button
                  onClick={handleCloseFollowUpModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Follow-up Form */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">
                      {isEditingFollowUp ? 'Edit Follow-up' : 'Create New Follow-up'}
                    </h4>
                    {isEditingFollowUp && (
                      <button
                        onClick={handleCreateFollowUp}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        New Follow-up
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Date *
                      </label>
                      <input
                        type="date"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Type *
                      </label>
                      <select
                        value={followUpType}
                        onChange={(e) => setFollowUpType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="Call">Call</option>
                        <option value="Email">Email</option>
                        <option value="Meeting">Meeting</option>
                        <option value="WhatsApp">WhatsApp</option>
                        <option value="Visit">Visit</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes *
                      </label>
                      <textarea
                        value={followUpNotes}
                        onChange={(e) => setFollowUpNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter follow-up notes..."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Outcome
                      </label>
                      <select
                        value={followUpOutcome}
                        onChange={(e) => setFollowUpOutcome(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Select Outcome</option>
                        <option value="Interested">Interested</option>
                        <option value="Not Interested">Not Interested</option>
                        <option value="Follow-up Required">Follow-up Required</option>
                        <option value="Converted">Converted</option>
                        <option value="Lost">Lost</option>
                        <option value="No Response">No Response</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Next Follow-up Date
                      </label>
                      <input
                        type="date"
                        value={nextFollowUpDate}
                        onChange={(e) => setNextFollowUpDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div className="flex justify-end space-x-2 pt-2">
                      <button
                        onClick={handleCloseFollowUpModal}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveFollowUp}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {isEditingFollowUp ? 'Update' : 'Create'} Follow-up
                      </button>
                    </div>
                  </div>
                </div>

                {/* Follow-up History */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Follow-up History</h4>
                  
                  {loadingFollowUps ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : followUps.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-xs">No follow-ups yet</p>
                      <p className="text-xs">Create your first follow-up</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                      {followUps.map((followUp) => {
                        // Handle date conversion - API may return string or Date
                        const followUpDateValue = followUp.followUpDate instanceof Date 
                          ? followUp.followUpDate 
                          : new Date(followUp.followUpDate);
                        const nextDateValue = followUp.nextFollowUpDate 
                          ? (followUp.nextFollowUpDate instanceof Date 
                              ? followUp.nextFollowUpDate 
                              : new Date(followUp.nextFollowUpDate))
                          : null;
                        const createdAtValue = followUp.createdAt instanceof Date 
                          ? followUp.createdAt 
                          : new Date(followUp.createdAt);
                        
                        return (
                          <div
                            key={followUp.id}
                            className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-indigo-300 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-1.5">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-xs font-medium text-gray-900">
                                    {followUpDateValue.toLocaleDateString()}
                                  </span>
                                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                                    {followUp.followUpType}
                                  </span>
                                </div>
                                {followUp.outcome && (
                                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 mb-1.5 inline-block">
                                    {followUp.outcome}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleEditFollowUp(followUp)}
                                className="text-indigo-600 hover:text-indigo-800 transition-colors flex-shrink-0 ml-2"
                                title="Edit Follow-up"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <p className="text-xs text-gray-700 mb-1.5 line-clamp-2">{followUp.notes}</p>
                            {nextDateValue && (
                              <div className="text-xs text-gray-500 mb-1">
                                Next: {nextDateValue.toLocaleDateString()}
                              </div>
                            )}
                            <div className="text-xs text-gray-400">
                              By {followUp.createdByName} on {createdAtValue.toLocaleDateString()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage; 
